import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { Roles } from '@/api/core-api';
import {
  type CoreApiMutationResult,
  toCoreApiMutationResult,
} from '@/utils/core-api-mutation-result';
import { serverFetch } from '@/utils/server-fetch';
import { getServerSession } from '@/utils/session/get-server-session';

/**
 * The researcher's calendar defaults: applied to every new project calendar
 * (the calendar editor is still the legacy one, which reads them). Stored on
 * `user.scheduler.defaultSettings` and written through the legacy core-api
 * `PUT /users/:id/scheduler`, which merges rather than replaces.
 */

/** Minutes, as offered by the legacy page. */
export const BUFFER_MINUTES = [
  0, 5, 10, 15, 20, 30, 45, 60, 90, 120, 150, 180,
] as const;

export const MAX_SCHEDULING_NOTICE_HOURS = 48;

export interface CalendarDefaults {
  /** `null` means no daily limit. */
  maxBookingsPerDay: number | null;
  bufferBeforeEvent: number | null;
  bufferAfterEvent: number | null;
  minimumSchedulingNotice: number | null;
}

export type CalendarDefaultsResult = CoreApiMutationResult;

const storedNumber = z.number().nullish().catch(undefined);

const rawUserSchema = z.object({
  scheduler: z
    .object({
      defaultSettings: z
        .object({
          maxBookingsPerDay: storedNumber,
          bufferBeforeEvent: storedNumber,
          bufferAfterEvent: storedNumber,
          minimumSchedulingNotice: storedNumber,
        })
        .nullish()
        .catch(undefined),
    })
    .nullish()
    .catch(undefined),
});

async function requireResearcherId(): Promise<string | null> {
  const session = await getServerSession();
  const user = session?.user;
  if (!user || !user.roles?.includes(Roles.RESEARCHER)) return null;
  return user.id;
}

export const getCalendarDefaults = createServerFn().handler(
  async (): Promise<CalendarDefaults> => {
    const userId = await requireResearcherId();
    if (!userId) throw new Error('Not signed in as a researcher');

    const response = await serverFetch({
      path: `/v4/users/${encodeURIComponent(userId)}`,
      userId,
    });
    if (!response.ok) {
      throw new Error(`Failed to load calendar defaults (${response.status})`);
    }
    // Only the four numbers leave the server: the document also carries the
    // scheduler token and OAuth credentials.
    const settings = rawUserSchema.parse(await response.json()).scheduler
      ?.defaultSettings;
    return {
      maxBookingsPerDay: settings?.maxBookingsPerDay ?? null,
      bufferBeforeEvent: settings?.bufferBeforeEvent ?? null,
      bufferAfterEvent: settings?.bufferAfterEvent ?? null,
      minimumSchedulingNotice: settings?.minimumSchedulingNotice ?? null,
    };
  },
);

const bufferSchema = z
  .number()
  .refine((value) => BUFFER_MINUTES.some((minutes) => minutes === value));

export const calendarDefaultsSchema = z.object({
  maxBookingsPerDay: z.number().int().min(1).max(100).nullable(),
  bufferBeforeEvent: bufferSchema.nullable(),
  bufferAfterEvent: bufferSchema.nullable(),
  minimumSchedulingNotice: z
    .number()
    .int()
    .min(0)
    .max(MAX_SCHEDULING_NOTICE_HOURS)
    .nullable(),
});

export const saveCalendarDefaults = createServerFn({ method: 'POST' })
  .validator((data: CalendarDefaults) => calendarDefaultsSchema.parse(data))
  .handler(async ({ data }): Promise<CalendarDefaultsResult> => {
    const userId = await requireResearcherId();
    if (!userId) return { success: false };

    // Only the daily limit can be cleared; the others reject `null`, so an
    // unset value is left out and keeps whatever is stored. The form never
    // sends a cleared scheduling notice once one is stored, so leaving it out
    // does not drop a change the researcher made.
    const body: Record<string, number | null> = {
      maxBookingsPerDay: data.maxBookingsPerDay,
    };
    if (data.bufferBeforeEvent !== null) {
      body.bufferBeforeEvent = data.bufferBeforeEvent;
    }
    if (data.bufferAfterEvent !== null) {
      body.bufferAfterEvent = data.bufferAfterEvent;
    }
    if (data.minimumSchedulingNotice !== null) {
      body.minimumSchedulingNotice = data.minimumSchedulingNotice;
    }

    const response = await serverFetch({
      path: `/users/${encodeURIComponent(userId)}/scheduler`,
      method: 'PUT',
      userId,
      body: JSON.stringify(body),
    });
    return toCoreApiMutationResult(response, 'saveCalendarDefaults');
  });
